// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.OperatorValueSelectBoxLazyLoading.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
// </copyright>
// ----------------------------------------------------------------------

/**
 * OperatorValueSelectBox component
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import connect from 'share/enhancers/Izenda.Connect';
import constants from 'logic/data/Izenda.FilterDetailsConfigConstants';
import Validator from 'share/components/hoc/Izenda.Validator';
import Input from 'share/components/controls/Izenda.Input';
import classnames from 'classnames';
import {
  identity,
  get,
  find,
  isEmpty,
  difference,
  filter,
  uniqBy,
  flow,
  flatMap,
  isNil,
  isArray,
  map,
  last,
  isObject,
  placeholder,
  head
} from 'lodash/fp';
import _Select from 'share/components/controls/multiSelectBox/Izenda.Select';

const Select = Validator()(_Select);
const {
  DEFAUFT_VALUE: { ALL, NULL, BLANK }
} = constants;
const EMPTY = '';
const DATA_TYPE = constants.DATA_TYPE;

class OperatorValueSelectBox extends Component {
  static propTypes = {
    filterSource: PropTypes.object.isRequired,
    filterInfo: PropTypes.object.isRequired,
    viewMode: PropTypes.bool,
    applyFormater: PropTypes.func
  };

  static defaultProps = {
    applyFormater: identity
  };

  componentWillUnmount() {
    this.props.configField.resetOperatorSourceDisplay();
  }

  /**
   * get error message by key
   * @param  {Object} object error message collection
   * @param  {string} key    key
   * @return {string}
   */
  getDataByKey = (object: any, key: string) => placeholder(object).get(key);

  /**
   * Handle switch component to select multiple or signle
   * @param  {Object} e React event
   */
  switchToSingleOrMultiple = (value: any) => {
    const props = this.props;
    const {
      validation,
      operatorGroup,
      operatorGroup: {
        fieldDetails: { operatorSetting }
      },
      filterInfo: { value: sourceValue }
    } = props;

    validation.reset();

    const isMultipleMode = this.checkMultipleMode(operatorSetting).isMultiple;
    const multiTypeMode = this.checkMultipleMode(operatorSetting).multipleType;
    const isMultipleCheck = this.checkMultipleMode(value).isMultiple;
    const multiTypeCheck = this.checkMultipleMode(value).multipleType;

    const values = this.getValueFromSource(sourceValue.sort()) || [];
    let result = [];

    if (isMultipleMode === isMultipleCheck && multiTypeMode === multiTypeCheck) {
      return;
    }
    if (isMultipleCheck) {
      if (isArray(values)) {
        result = values.map(data => data.value);
      } else {
        result = isMultipleCheck ? [values.value] : [];
      }
    }

    props.onChange && props.onChange(result);
    operatorGroup.fieldDetails.setOperatorSetting(value);
  };

  removeOption = (keyValue: string) => {
    const data = get('configField.operatorSourceDisplay.data', this.props);
    const firstItem = find({ key: keyValue }, data);
    data.remove(firstItem);
  };

  /**
   * Handle switch component to single
   */
  switchToSingle = () => {
    this.removeOption(ALL);
    this.switchToSingleOrMultiple(constants.SELECT_SETTINGS.SINGLE);
  };

  /**
   * Handle switch component to multiple
   */
  switchToMultiple = (mode) => {
    const data = get('configField.operatorSourceDisplay.data', this.props);
    if (!isEmpty(data) && !isEmpty(difference(flatMap('key', data), [NULL, BLANK, EMPTY]))) {
      const startIndex = 0;
      const deleteCount = 0;
      data.splice(startIndex, deleteCount, {
        key: ALL,
        value: ALL,
        label: ALL
      });
    }
    this.switchToSingleOrMultiple(mode);
  };

  /**
   * Handle when change value of selectbox
   * @param  {Array<Object>} selectValues list select value
   */
  onChangeSelect = (selectValues: Array<any>) => {
    const props = this.props;
    // when remove value
    if (isEmpty(selectValues)) {
      props.onChange && props.onChange([]);
      return;
    }
    let result = [];
    const {
      operatorGroup: {
        fieldDetails: { operatorSetting }
      }
    } = props;
    const isMultipleMode = this.checkMultipleMode(operatorSetting).isMultiple;
    const multiType = this.checkMultipleMode(operatorSetting).multipleType;

    if (isMultipleMode) {
      result =
        last(selectValues).value === ALL
          ? [ALL]
          : flow(
            uniqBy('value'),
            filter((item: any) => item.value !== ALL && !isEmpty(item.value)),
            map((item: any) => item.value)
          )(selectValues);
    } else {
      result = isNil(selectValues) ? [] : [selectValues.value];
    }

    if (isArray(selectValues)) {
      if (get('isNew', last(selectValues))) {
        this.resetSearchData();
      }
    } else if (isObject(selectValues) && selectValues.isNew) {
      this.resetSearchData();
    }

    props.onChange && props.onChange(result);
  };

  /**
   * Handle when add new value of selectbox
   * @param  {Object} newValue new value of select box
   */
  onAddNewSelect = (newValue: any) => {
    const {
      isSelect,
      operatorGroup,
      validation: valManager,
      index
    } = this.props;
    if (isSelect) {
      return false;
    }
    const dataType = operatorGroup.fieldDetails.useLookup ? constants.DATA_TYPE.TEXT : operatorGroup.fieldDetails.dataType;
    const errorMessage = this.getDataByKey(constants.ERROR_MESSAGE_BY_DATA_TYPE, dataType);
    if (!this.validationValue(newValue.value, dataType)) {
      valManager.updateErrors([{ key: constants.CONSTRAINTS_NAME + index, messages: errorMessage }]);
      return false;
    }

    return true;
  };

  /**
   * Validation when add new value for selectbox
   * @param  {Object} value    validate value
   * @param  {string} dataType data type of value
   * @return {boolean}         result of validate
   */
  validationValue(value: any, dataType: string) {
    switch (dataType) {
      case constants.DATA_TYPE.TEXT:
        return true;
      case constants.DATA_TYPE.TIME:
        return String.isPatternFormat(value, constants.REGEX.TIME);
      case constants.DATA_TYPE.MONEY:
      case constants.DATA_TYPE.NUMBER:
        return String.isPatternFormat(value, constants.REGEX.NUMBER);
      case constants.DATA_TYPE.DATE:
        return String.isPatternFormat(value, constants.REGEX.DATETIMEFORMAT);
      default:
        return true;
    }
  }

  /**
   * Get value from source for selectbox
   * @param  {Obect} source value
   * @return         array or Object base on component multiple or single settings
   */
  getValueFromSource = (source: any) => {
    let result;
    const {
      operatorGroup: {
        fieldDetails: { operatorSetting }
      },
      applyFormater
    } = this.props;
    const isMultipleMode = this.checkMultipleMode(operatorSetting).isMultiple;

    if (isMultipleMode) {
      const oneItemArraySize = 1;
      result = isArray(source)
        ? map(
          (item: any) => ({
            value: item,
            label: applyFormater(item)
          }),
          source
        )
        : [];
      // Remove empty value
      // if (result.length === oneItemArraySize && isEmpty(head(result).value)) {
      //   result = [];
      // }
    } else {
      const firstSourceItem = head(source);
      result =
        isArray(source) && !isEmpty(firstSourceItem)
          ? { value: firstSourceItem, label: applyFormater(firstSourceItem) }
          : null;
    }
    return result;
  };

  /*
   */
  checkMultipleMode = (value: string) => {
    return {
      isMultiple: !!value && !(value === constants.SELECT_SETTINGS.SINGLE),
      multipleType: value
    }
  };

  renderSelectBox = () => {
    const {
      operatorGroup: {
        fieldDetails: { operatorSetting, dataType, isParameter }
      },
      viewMode,
      disabled,
      localization
    } = this.props;
    const isMultipleMode = this.checkMultipleMode(operatorSetting).isMultiple;
    const multiType = this.checkMultipleMode(operatorSetting).multipleType;
    return viewMode ? null : (
      <div>
        <div className='u_inlineBlock'>
          <Input
            type='radio'
            checked={!isMultipleMode}
            onChange={this.switchToSingle}
            value={constants.SELECT_SETTINGS.SINGLE}
            disabled={disabled}
          />
          {constants.SELECT_MODE.SINGLE} &nbsp;
         </div>
        {(dataType === 'Text' || !isParameter) && (
          <div className='u_inlineBlock'>
            <Input
              type='radio'
              checked={isMultipleMode}
              onChange={() => this.switchToMultiple(constants.SELECT_SETTINGS.MULTIPLE)}
              value={constants.SELECT_SETTINGS.MULTIPLE}
              disabled={disabled}
            />
            {constants.SELECT_MODE.MULTIPLE} &nbsp;
             </div>
        )}
        {
          ((dataType === 'Text' || !isParameter) && (isMultipleMode)) && (
            <div className="izenda-mt-10">
              <p>{localization.getText('REPORTDESIGNER_LABEL_DELIMITER_VALUE')}</p>
              <div className='u_inlineBlock'>
                <Input
                  type='radio'
                  checked={multiType === constants.SELECT_SETTINGS.MULTIPLE}
                  onChange={() => this.switchToMultiple(constants.SELECT_SETTINGS.MULTIPLE)}
                  value={constants.SELECT_SETTINGS.MULTIPLE}
                  disabled={false}
                />
                {constants.SELECT_MODE.MULTIPLE_NONE} &nbsp;
               </div>
              <div className='u_inlineBlock'>
                <Input
                  type='radio'
                  checked={multiType === constants.SELECT_SETTINGS.MULTIPLE_COMMA}
                  onChange={() => this.switchToMultiple(constants.SELECT_SETTINGS.MULTIPLE_COMMA)}
                  value={constants.SELECT_SETTINGS.MULTIPLE_COMMA}
                  disabled={false}
                />
                {constants.SELECT_MODE.MULTIPLE_COMMA} &nbsp;
               </div>
              <div className='u_inlineBlock'>
                <Input
                  type='radio'
                  checked={multiType === constants.SELECT_SETTINGS.MULTIPLE_NEWLINE}
                  onChange={() => this.switchToMultiple(constants.SELECT_SETTINGS.MULTIPLE_NEWLINE)}
                  value={constants.SELECT_SETTINGS.MULTIPLE_NEWLINE}
                  disabled={false}
                />
                {constants.SELECT_MODE.MULTIPLE_NEW_LINE} &nbsp;
               </div>
            </div>
          )}
      </div>
    );
  };
  /**
   * When API returns data, check if there's values; otherwise, remove option [ALL]
   */
  checkReturnOptions = () => {
    const data = get('configField.operatorSourceDisplay.data', this.props);
    const firstItem = find({ key: ALL }, data);

    if (firstItem) {
      const fieldDetails = get('operatorGroup.fieldDetails', this.props);
      if (isNil(fieldDetails.operatorSetting)) {
        fieldDetails.setOperatorSetting(constants.SELECT_SETTINGS.SINGLE);
      }
      if (
        fieldDetails.isParameter &&
        !isEmpty(data) &&
        isEmpty(difference(flatMap('key', data), [ALL, NULL, BLANK, EMPTY]))
      ) {
        data.remove(firstItem);
      }
    }

    return data;
  };

  getData = (pageInfo: any, isSearching: boolean) => {
    const { store, configField } = this.props;
    return store.getData(pageInfo, configField, this.searchKey, isSearching).then(this.checkReturnOptions);
  };

  resetSearchData = () => {
    if (this.searchKey) {
      this.searchKey = null;
      this.props.configField.resetOperatorSourceDisplay();
    }
  };

  handleSearchData = (key: string, pageInfo: any) => {
    this.searchKey = key;
    return this.getData(pageInfo, true);
  };


  onTextAreaChange(e) {
    const props = this.props;
    const {
      operatorGroup: {
        fieldDetails: { operatorSetting, dataType, isParameter }
      },
    } = props;
    const multiType = this.checkMultipleMode(operatorSetting).multipleType;
    let selectValues = [];
    let data = e.target.value;
    if (multiType === constants.SELECT_SETTINGS.MULTIPLE_NEWLINE) {
      selectValues = data
        .replace(/\n/g, constants.DELIMITER_TYPE.NEWLINE)
        .split(constants.DELIMITER_TYPE.NEWLINE)
    } else {
      selectValues = data.split(',');
    }
    selectValues = selectValues
      .map(data => data.trim())
      //  .filter(data => data)
    props.onChange && props.onChange(selectValues);
  }

  onTextAreaKeyPress(e) {
    if (e && e.keyCode == 13) {
      this.props.store.reportStore.alertDetails.set({
        success: true,
        message: 'Filter values set successfully',
        showType: 'reset',
      })
      this.props.closePopover && this.props.closePopover();
      return;
    }
  }

  getNewLineValues(values) {
    return values.map(data => data.value).join('\n');
  }

  getCommaValues(values) {
    return values.map(data => data.value).join(',');
  }

  // onCommaValueChange(e) {
  //   const props = this.props;
  //   let result = [];
  //   let data = e.target.value;
  //   let actualData = data.split(',');
  //   let selectValues = map(innerData => ({ value: innerData, label: innerData }), actualData);
  //   selectValues = flatten(selectValues)
  //   result =
  //     last(selectValues).value === ALL
  //       ? [ALL]
  //       : flow(
  //         // uniqBy('value'),
  //         //  filter((item: any) => item.value !== ALL && !isEmpty(item.value)),
  //         filter((item: any) => item.value !== ALL),
  //         map((item: any) => item.value)
  //       )(selectValues);
  //   props.onChange && props.onChange(result);
  // }

  renderInputField() {
    const {
      filterInfo: { value: sourceValue },
      operatorGroup: {
        fieldDetails: { operatorSetting }
      },
      configField: {
        operatorSourceDisplay: { data, isLastPage }
      },
      labelField,
      valueField,
      isSelect,
      index,
      disabled,
      localization
    } = this.props;
    const isMultipleMode = this.checkMultipleMode(operatorSetting).isMultiple;
    const multiType = this.checkMultipleMode(operatorSetting).multipleType;
    const values = this.getValueFromSource(sourceValue);
    const constraints = constants.CONSTRAINTS;
    const textAreaStyle = classnames({ 'izenda-mt-10': !this.props.closePopover }, { 'izenda-w-50': this.props.closePopover })
    if (multiType === constants.SELECT_SETTINGS.MULTIPLE_NEWLINE) {
      return (
        <div className={textAreaStyle}>
          <textarea
            className="form-control"
            placeholder={localization.getText('REPORTDESIGNER_NEWLINE_DELIMITER_PLACEHOLDER')}
            rows="3"
            onChange={(e) => this.onTextAreaChange(e)}
            value={this.getNewLineValues(values)}
          //  onKeyDown={(e) => this.onTextAreaKeyPress(e)}
          />
        </div>
      )
    } else if (multiType === constants.SELECT_SETTINGS.MULTIPLE_COMMA) {
      return (
        <div className={textAreaStyle}>
          <textarea
            className="form-control"
            placeholder={localization.getText('REPORTDESIGNER_NEWLINE_DELIMITER_PLACEHOLDER')}
            rows="3"
            onChange={(e) => this.onTextAreaChange(e)}
            value={this.getCommaValues(values)}
          />
        </div>
      )
    } else {
      return (
        <div className='select-box-container'>
          <Select
            multi={isMultipleMode}
            options={data}
            labelKey={labelField}
            valueKey={valueField}
            scalable={false}
            value={values}
            onChange={this.onChangeSelect}
            onAddNew={isSelect ? identity : this.onAddNewSelect}
            field={constants.CONSTRAINTS_NAME + index}
            validations={constraints}
            disabled={disabled}
            inputMaxHeight={85}
            onCloseMenu={this.resetSearchData}
            getData={this.getData}
            onSearchLazy={this.handleSearchData}
            isLastPage={isLastPage}
            isLazyLoading
          />
        </div>
      )
    }
  }

  /**
   * Render component
   */
  render() {
    const {
      configField: {
        operatorSourceDisplay: { data, isLastPage }
      }
    } = this.props;
    // HACK: Define data length to mobx 2.4.x trigger rendered
    // NOSONAR
    const dataLength = data.length; // eslint-disable-line no-unused-vars
    return (
      <div className='manual-entry'>
        {this.renderSelectBox()}
        {this.renderInputField()}
      </div>
    );
  }
}

export default connect()(OperatorValueSelectBox);



// WEBPACK FOOTER //
// ./ui/modules/OperatorValue/Izenda.OperatorValueSelectBoxLazyLoading.js